import metaApiClient from './meta-client';

export interface HourlyClick {
    hour: number;
    clicks: number;
    unique_leads: number;
}

export interface BroadcastResult {
    id: number;
    title: string;
    total_pages: number;
    status: string;
    created_at: string | null;
    completed_at: string | null;
    total_leads: number;
    total_sent: number;
    total_delivered: number;
    delivery_rate: number;
    total_clicks: number;
    unique_leads_clicked: number;
    ctr: number;
    daily_clicks: { date: string; clicks: number; unique_leads: number }[];
    hourly_clicks: HourlyClick[];
}

export interface FunnelResult {
    id: number;
    title: string;
    total_clicks: number;
    unique_leads: number;
    daily_clicks: { date: string; clicks: number; unique_leads: number }[];
    hourly_clicks: HourlyClick[];
}

export interface FunnelsDashboardData {
    summary: {
        total_funnels: number;
        total_clicks: number;
        total_unique_leads: number;
    };
    results: FunnelResult[];
    daily_clicks: DailyClick[];
    hourly_clicks: HourlyClick[];
    period: { start: string; end: string };
}

export interface FunnelsDashboardResponse {
    success: boolean;
    data: FunnelsDashboardData;
}

export interface DailyClick {
    date: string;
    clicks: number;
    unique_leads: number;
}

export interface MetaDashboardData {
    summary: {
        total_broadcasts: number;
        total_sent: number;
        total_delivered: number;
        total_clicks: number;
        delivery_rate: number;
    };
    results: BroadcastResult[];
    daily_clicks: DailyClick[];
    hourly_clicks: HourlyClick[];
    period: { start: string; end: string };
}

export interface MetaDashboardResponse {
    success: boolean;
    data: MetaDashboardData;
}

export const getMetaDashboard = async (
    start: string,
    end: string,
    categoryId?: number
): Promise<MetaDashboardResponse> => {
    const response = await metaApiClient.post<MetaDashboardResponse>(
        '/meta/analytics/dashboard',
        { start, end, ...(categoryId !== undefined && { category_id: categoryId }) }
    );
    return response.data;
};

export interface CountryClick {
    country: string;
    clicks: number;
    unique_leads: number;
}

export interface DetailData {
    group_id?: number;
    funnel_id?: number;
    hourly_clicks: HourlyClick[];
    country_clicks: CountryClick[];
    period: { start: string; end: string };
}

export interface DetailResponse {
    success: boolean;
    data: DetailData;
}

export const getBroadcastGroupDetail = async (
    groupId: number,
    start: string,
    end: string
): Promise<DetailResponse> => {
    const response = await metaApiClient.post<DetailResponse>(
        `/meta/analytics/dashboard/${groupId}`,
        { start, end }
    );
    return response.data;
};

export const getFunnelDetail = async (
    funnelId: number,
    start: string,
    end: string
): Promise<DetailResponse> => {
    const response = await metaApiClient.post<DetailResponse>(
        `/meta/analytics/funnels/${funnelId}`,
        { start, end }
    );
    return response.data;
};

export const getFunnelsDashboard = async (
    start: string,
    end: string,
    categoryId?: number
): Promise<FunnelsDashboardResponse> => {
    const response = await metaApiClient.post<FunnelsDashboardResponse>(
        '/meta/analytics/funnels',
        { start, end, ...(categoryId !== undefined && { category_id: categoryId }) }
    );
    return response.data;
};

// ── Tipos de revenue ──────────────────────────────────────────────────────────
export interface BroadcastSummary {
    group_id: number;
    total_pages: number;
    total_leads: number;
    total_sent: number;
    total_failed: number;
    total_delivered: number;
    delivery_rate: number;
    total_clicks: number;
    unique_clicked: number;
    ctr: number;
    pages_by_domain: { domain: string; pages: number; sent: number }[];
}

export interface RevenueDomain {
    domain: string;
    revenue: number;
    impressions: number;
    clicks: number;
    ecpm: number;
    ctr: number;
}

export interface RevenueDate {
    date: string;
    domain: string;
    revenue: number;
    impressions: number;
    clicks: number;
    ecpm: number;
    ctr: number;
}

export interface BroadcastRevenue {
    group_id: number;
    domains: string[];
    total_revenue: number;
    total_impressions: number;
    total_clicks: number;
    avg_ecpm: number;
    avg_ctr: number;
    by_domain: RevenueDomain[];
    by_date: RevenueDate[];
    period: { start: string; end: string };
}

export const getBroadcastSummary = async (groupId: number): Promise<{ success: boolean; data: BroadcastSummary }> => {
    const r = await metaApiClient.get(`/meta/analytics/broadcast/${groupId}/summary`);
    return r.data;
};

export const getBroadcastRevenue = async (
    groupId: number, start: string, end: string
): Promise<{ success: boolean; data: BroadcastRevenue | null; error?: string }> => {
    const r = await metaApiClient.get(`/meta/analytics/broadcast/${groupId}/revenue`, { params: { start, end } });
    return r.data;
};

export interface DomainHealth {
    domain: string;
    total_pages: number;
    total_groups: number;
    total_sent: number;
    total_failed: number;
    failure_rate: number;
    has_revenue: boolean;
    revenue: number;
    impressions: number;
    ecpm: number;
    ad_clicks: number;
}

export const getDomainHealth = async (
    start: string, end: string, categoryId?: number
): Promise<{ success: boolean; data: DomainHealth[] }> => {
    const r = await metaApiClient.get('/meta/analytics/domains/health', {
        params: { start, end, ...(categoryId !== undefined && { category_id: categoryId }) },
    });
    return r.data;
};

// ── Revenue batch — 1 req para N grupos ──────────────────────────────────────
export interface GroupRevenueBatch {
    group_id: number;
    total_revenue: number;
    total_impressions: number;
    total_clicks: number;
    by_domain: RevenueDomain[];
    by_date: RevenueDate[];
}

export interface RevenueBatchMeta {
    groups_queried: number;
    domains_queried: number;
    groups_with_revenue: number;
    period: { start: string; end: string };
}

export const getRevenueBatch = async (
    start: string,
    end: string,
    group_ids?: number[],
    categoryId?: number
): Promise<{ success: boolean; data: GroupRevenueBatch[] | null; meta?: RevenueBatchMeta; error?: string }> => {
    const r = await metaApiClient.post('/meta/analytics/revenue/batch', {
        start, end, group_ids, ...(categoryId !== undefined && { category_id: categoryId }),
    });
    return r.data;
};

// ── Geo-revenue: matriz país × domain ────────────────────────────────────────
export interface GeoRevenueCell {
    domain: string;
    country: string;
    revenue: number;
    impressions: number;
    clicks: number;
    ecpm: number;
    ctr: number;
    rpc: number;
    has_revenue: boolean;
}

export interface GeoRevenueTotals {
    revenue: number;
    impressions: number;
    clicks: number;
}

export interface GeoRevenueDomainTotals extends GeoRevenueTotals {
    has_revenue: boolean;
    ecpm: number;
}

export interface GeoRevenueData {
    cells: GeoRevenueCell[];
    domains: string[];
    countries: string[];
    no_revenue_domains: string[];
    country_totals: Record<string, GeoRevenueTotals>;
    domain_totals: Record<string, GeoRevenueDomainTotals>;
    period: { start: string; end: string };
}

export const getGeoRevenue = async (
    start: string, end: string, categoryId?: number
): Promise<{ success: boolean; data: GeoRevenueData | null; error?: string }> => {
    const r = await metaApiClient.post('/meta/analytics/geo-revenue', {
        start, end, ...(categoryId !== undefined && { category_id: categoryId }),
    });
    return r.data;
};

// ── Funnel revenue batch ──────────────────────────────────────────────────────
export interface FunnelRevenueBatch {
    funnel_id: number;
    total_revenue: number;
    total_impressions: number;
    total_clicks: number;
    by_domain: RevenueDomain[];
    by_date: RevenueDate[];
}

export const getFunnelRevenueBatch = async (
    start: string,
    end: string,
    funnel_ids?: number[],
    categoryId?: number
): Promise<{ success: boolean; data: FunnelRevenueBatch[] | null; error?: string }> => {
    const r = await metaApiClient.post('/meta/analytics/funnels/revenue/batch', {
        start, end, funnel_ids, ...(categoryId !== undefined && { category_id: categoryId }),
    });
    return r.data;
};

export const getFunnelGeoRevenue = async (
    start: string, end: string
): Promise<{ success: boolean; data: GeoRevenueData | null; error?: string }> => {
    const r = await metaApiClient.post('/meta/analytics/funnels/geo-revenue', { start, end });
    return r.data;
};

export interface NodeStat {
    node_id: number;
    nome: string;
    tipo: string;
    delay_seconds: number | null;
    goto_funnel_id: number | null;
    clicks: number;
    unique_leads: number;
    leads_waiting: number;
}

export interface NodeTransition {
    from_node_id: number;
    to_node_id: number;
}

export interface NodeFlowData {
    funnel_id: number;
    nodes: NodeStat[];
    transitions: NodeTransition[];
    period: { start: string; end: string };
}

export interface NodeFlowResponse {
    success: boolean;
    data: NodeFlowData;
}

export const getFunnelNodes = async (
    funnelId: number,
    start: string,
    end: string
): Promise<NodeFlowResponse> => {
    const response = await metaApiClient.post<NodeFlowResponse>(
        `/meta/analytics/funnels/${funnelId}/nodes`,
        { start, end }
    );
    return response.data;
};
